import React, { useState } from 'react';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';
import Badge from '../../../components/ui/Badge';
import { formatDate } from '../../../utils/formatters';

const TYPE_BADGE = {
  paper: { variant: 'info', label: 'Paper Session' },
  keynote: { variant: 'primary', label: 'Keynote' },
  invited: { variant: 'primary', label: 'Invited' },
  plenary: { variant: 'primary', label: 'Plenary' },
  panel: { variant: 'info', label: 'Panel' },
  break: { variant: 'warning', label: 'Break' },
  social: { variant: 'secondary', label: 'Social' },
  opening: { variant: 'success', label: 'Opening' },
  closing: { variant: 'success', label: 'Closing' },
  workshop: { variant: 'warning', label: 'Workshop' },
  tutorial: { variant: 'info', label: 'Tutorial' },
  poster: { variant: 'info', label: 'Poster' },
  other: { variant: 'default', label: 'Other' },
};

const BREAK_TYPES = ['break', 'social', 'opening', 'closing'];

const ProgramSection = ({ sessions, startDate, endDate, tracks }) => {
  const conferenceDays = getConferenceDays(startDate, endDate);
  const [selectedDay, setSelectedDay] = useState(conferenceDays?.[0]?.date || '');
  const [filterTrack, setFilterTrack] = useState('all');
  const [filterType, setFilterType] = useState('all');

  const daySessions = sessions
    ?.filter(s => s?.sessionDate === selectedDay)
    ?.filter(s => filterTrack === 'all' || s?.trackId === filterTrack || BREAK_TYPES.includes(s?.sessionType))
    ?.filter(s => filterType === 'all' || s?.sessionType === filterType)
    ?.sort((a, b) => (a?.startTime || '').localeCompare(b?.startTime || ''));

  const formatTime = (t) => {
    if (!t) return '';
    const parts = t?.substring(0, 5)?.split(':');
    const h = parseInt(parts?.[0]);
    const m = parts?.[1];
    const ampm = h >= 12 ? 'PM' : 'AM';
    const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
    return `${h12}:${m} ${ampm}`;
  };

  const uniqueTypes = [...new Set(sessions?.map(s => s?.sessionType))]?.filter(Boolean);

  if (!sessions?.length) return null;

  return (
    <section className="py-4 md:py-6">
      <div className="flex items-center gap-3 mb-5">
        <div className="w-10 h-10 md:w-12 md:h-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
          <Icon name="Calendar" size={20} className="text-primary" />
        </div>
        <h2 className="text-xl md:text-2xl font-bold text-foreground">Conference Program</h2>
      </div>

      {/* Day tabs */}
      <div className="flex gap-2 overflow-x-auto pb-2 mb-4">
        {conferenceDays?.map(day => (
          <button
            key={day.date}
            onClick={() => setSelectedDay(day.date)}
            className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${
              selectedDay === day.date
                ? 'bg-primary text-primary-foreground shadow-sm'
                : 'bg-card border border-border text-foreground hover:bg-muted'
            }`}
          >
            Day {day.dayNumber}: {day.label}
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-5">
        <Select
          value={filterTrack}
          onChange={setFilterTrack}
          className="min-w-[11rem]"
          options={[
            { value: 'all', label: 'All tracks' },
            ...(tracks || []).map((tr) => ({ value: tr?.id, label: tr?.name })),
          ]}
        />
        <Select
          value={filterType}
          onChange={setFilterType}
          className="min-w-[11rem]"
          options={[
            { value: 'all', label: 'All types' },
            ...(uniqueTypes || []).map((type) => ({ value: type, label: TYPE_BADGE[type]?.label || type })),
          ]}
        />
      </div>

      {/* Sessions */}
      {daySessions?.length === 0 ? (
        <div className="text-center py-8 bg-card border border-border rounded-lg">
          <Icon name="CalendarX" size={24} className="text-muted-foreground mx-auto mb-2" />
          <p className="text-sm text-muted-foreground">No sessions match your filters</p>
        </div>
      ) : (
        <div className="space-y-3">
          {daySessions?.map(session => {
            const isBreak = BREAK_TYPES.includes(session?.sessionType);

            if (isBreak) {
              return (
                <div key={session?.id} className="bg-warning-subtle/50 border border-warning-border rounded-lg px-4 py-3 text-center">
                  <div className="flex items-center justify-center gap-3">
                    <Icon name={session?.sessionType === 'break' ? 'Coffee' : session?.sessionType === 'social' ? 'PartyPopper' : 'Star'} size={16} className="text-warning" />
                    <span className="text-sm font-medium text-warning">{session?.title}</span>
                    <span className="text-xs font-mono text-warning">
                      {formatTime(session?.startTime)} – {formatTime(session?.endTime)}
                    </span>
                  </div>
                </div>
              );
            }

            return (
              <div key={session?.id} className="bg-card border border-border rounded-lg shadow-sm overflow-hidden hover:shadow-md transition-shadow">
                {/* Session header */}
                <div className="px-4 md:px-6 py-3 border-b border-border bg-muted/20">
                  <div className="flex items-center gap-3 flex-wrap">
                    <div className="flex items-center gap-1.5 text-sm font-mono text-muted-foreground">
                      <Icon name="Clock" size={14} />
                      {formatTime(session?.startTime)} – {formatTime(session?.endTime)}
                    </div>
                    {session?.room && (
                      <span className="text-xs px-2 py-0.5 rounded-md bg-muted text-muted-foreground">
                        {session?.room?.name}
                      </span>
                    )}
                    <Badge
                      variant={TYPE_BADGE[session?.sessionType]?.variant || 'default'}
                      size="sm"
                    >
                      {TYPE_BADGE[session?.sessionType]?.label || session?.sessionType}
                    </Badge>
                    {session?.track && (
                      <Badge variant="outline" size="sm">{session?.track?.name}</Badge>
                    )}
                  </div>
                  <h3 className="text-base md:text-lg font-semibold text-foreground mt-1">{session?.title}</h3>
                  {session?.chairName && (
                    <p className="text-xs text-muted-foreground mt-0.5">Chair: {session?.chairName}</p>
                  )}
                  {session?.description && (
                    <p className="text-xs text-muted-foreground mt-1">{session?.description}</p>
                  )}
                </div>

                {/* Items */}
                {session?.items?.length > 0 && (
                  <div className="divide-y divide-border">
                    {session?.items?.map((item, idx) => (
                      <div key={item?.id} className="px-4 md:px-6 py-3 flex items-start gap-3">
                        <span className="text-xs font-mono text-muted-foreground mt-0.5 w-5 flex-shrink-0 text-right">
                          {idx + 1}.
                        </span>
                        <div className="flex-1 min-w-0">
                          {item?.itemType === 'paper' && item?.paper ? (
                            <>
                              <div className="flex items-center gap-2">
                                <span className="text-xs font-mono text-primary font-semibold flex-shrink-0">
                                  #{item?.paper?.paperNumber}
                                </span>
                                <p className="text-sm font-medium text-foreground">{item?.paper?.title}</p>
                              </div>
                              <p className="text-xs text-muted-foreground mt-0.5">
                                {item?.paper?.author?.firstName} {item?.paper?.author?.lastName}
                                {item?.paper?.author?.institution && ` — ${item?.paper?.author?.institution}`}
                              </p>
                            </>
                          ) : (
                            <>
                              <div className="flex items-center gap-2">
                                <Icon name="Mic" size={12} className="text-violet-600 flex-shrink-0" />
                                <p className="text-sm font-medium text-foreground">{item?.customTitle}</p>
                              </div>
                              {item?.customSpeaker && (
                                <p className="text-xs text-muted-foreground mt-0.5">
                                  {item?.customSpeaker}
                                  {item?.customAffiliation && `, ${item?.customAffiliation}`}
                                </p>
                              )}
                            </>
                          )}
                        </div>
                        {item?.durationMinutes && (
                          <span className="text-xs text-muted-foreground flex-shrink-0">{item?.durationMinutes} min</span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
};

function getConferenceDays(startDate, endDate) {
  if (!startDate || !endDate) return [];
  const days = [];
  const start = new Date(startDate);
  const end = new Date(endDate);
  const current = new Date(start);
  while (current <= end) {
    days.push({
      date: current.toISOString().split('T')[0],
      label: formatDate(current, { weekday: 'short', month: 'short', day: 'numeric', year: undefined }),
      dayNumber: days.length + 1,
    });
    current.setDate(current.getDate() + 1);
  }
  return days;
}

export default ProgramSection;
